import React, { useState, useRef, useEffect } from 'react';
import styles from './SelectOption.module.scss';
import cx from 'classnames';
import useOutsideClick from '../../hooks/useOutsideClick';
import placeholder from '../../assets/placeholderTN.png';
import Icon from '../Icon/Icon';
import { useTranslation } from 'react-i18next';

const MIN_OPTIONS_HEIGHT = 200; // value in px

const MARGIN_FROM_SELECT = 10; // value in px


const SelectOption = ({
  id = null,
  parentRef,
  title = '',
  options = [],
  onSelect,
  currentlySelected,
  className,
  currentInputClassName,
  dropdownClassName,
  isMultiple,
  selectedIcon,
  unselectedIcon,
  windowRef = null,
}) => {
  const [showOptions, setShowOption] = useState(false);
  const [containerStyles, setContainerStyles] = useState({});

  const { t } = useTranslation();

  const handleSelect = option => {
    onSelect(option);

    if (!isMultiple) {
      setShowOption(false);
    }
  };

  const toggleSelection = () => setShowOption(prevState => !prevState);

  const selectRef = useRef(null);
  const containerRef = useRef(null);

  useOutsideClick(selectRef, () => {
    if (showOptions) {
      setShowOption(false);
    }
  });

  useEffect(() => {
    if (showOptions) {
      setTimeout(() => {
        const styles = { opacity: 1 };
        const optionsBoundaries = selectRef.current?.getBoundingClientRect() || {};
        const containerBoundaries = containerRef.current?.getBoundingClientRect() || {};

        // By default options are shown at the bottom, check if the height is not more than the parent container height
        if (parentRef?.current) {
          // Check height left till the bottom of the viewport 
          const possibleHeight = (windowRef?.current?.offsetHeight ?? window.innerHeight) - containerBoundaries.bottom - MARGIN_FROM_SELECT;
          // If options list height is higher than window height - move options to the top
          if (possibleHeight < MIN_OPTIONS_HEIGHT) {
            styles.transform = `translateY(calc(-100% - ${containerBoundaries.height + MARGIN_FROM_SELECT}px))`;
          } else {
            styles.maxHeight = `${possibleHeight - MARGIN_FROM_SELECT}px`;
          }
        } else {
          if (optionsBoundaries.top + optionsBoundaries.height > (windowRef?.current?.offsetHeight ?? window.innerHeight)) {
            const topMaxPossibleHeight = containerBoundaries.top - MARGIN_FROM_SELECT;
            styles.top = '100%';
            styles.transform = `translateY(-${(topMaxPossibleHeight > optionsBoundaries.height
              ? optionsBoundaries.height
              : topMaxPossibleHeight) +
              containerBoundaries.height +
              MARGIN_FROM_SELECT}px)`;
          }
        }

        setContainerStyles(styles);
      }, 100);
    } else {
      setContainerStyles(null);
    }
  }, [showOptions]);

  return (
    <div className={cx(styles.selectWrapper, className)} {...(id ? { id } : {})}>
      {title && <p className={styles.selectTitle}>{title}</p>}
      <div className={cx(styles.selectInput, currentInputClassName)} ref={containerRef} onClick={toggleSelection}>
        {currentlySelected ? (
          <div className={cx(styles.itemContainer, className)}>
            {currentlySelected?.name?.startsWith('#') && (
              <div className={styles.colorExample} style={{ background: currentlySelected?.name }}></div>
            )}
            {isMultiple ? (
              <span className={styles.multipleSpan}>
                {currentlySelected?.map(selected => t(selected?.key || selected?.title))?.join(', ') || '-'}
              </span>
            ) : (
              <span>{t(currentlySelected?.key || currentlySelected?.name || currentlySelected?.title || '-')}</span>
            )}
          </div>
        ) : (
            <span>{'-'}</span>
        )}
        <div className={styles.buttonWrapper}>
          <Icon src="arrowDown" alt="" className={cx(styles.arrowButton, { [styles.turnArrow]: showOptions })} />
        </div>
      </div>
      {showOptions && (
        <ul className={cx(styles.customSelectContainer, dropdownClassName)} ref={selectRef} style={containerStyles}>
          {options.length === 0 && <span className={styles.noOptions}>{t('general.no_options')}</span>}
          {options.map((option, index) => (
            option?.disabled ?
              null
              : <li className={styles.option} key={option?.id ? option?.id : option?.name || option?.title} onClickCapture={() => handleSelect(option)}>
              <div className={cx(styles.listItemContainer, className)}>
                {option.isImage ? (
                  <div className={styles.selectImageContainer}>
                    <img src={placeholder} className={styles.selectImage} alt={t('accessibility.placeholder_logo')} />
                    <div
                      className={cx(styles.selectImageLetter, {
                        [styles.backgroundStyle]: index === 0,
                        [styles.shadowStyle]: index === 1,
                        [styles.borderStyle]: index === 2,
                      })}
                    >
                        {'A'}
                    </div>
                  </div>
                ) : null}
                {selectedIcon &&
                  (isMultiple ? currentlySelected?.some(({ id }) => id === option?.id) : option?.id === currentlySelected?.id) && (
                    <Icon src={selectedIcon} className={styles.checkedIcon} />
                  )}
                {unselectedIcon &&
                  (isMultiple ? !currentlySelected?.some(({ id }) => id === option?.id) : option?.id !== currentlySelected?.id) && (
                    <Icon src={unselectedIcon} className={styles.checkedIcon} />
                  )}
                {option?.name?.startsWith('#') && <div className={styles.colorExample} style={{ background: option?.name }}></div>}
                  <span className={cx({ [styles.marginLeft]: selectedIcon && !unselectedIcon && option?.id !== currentlySelected?.id, [styles.withDescription]: option?.description })} style={{ '--after-content': `"${t(option?.description)}"` }}>
                  {t(option?.key || option?.name || option?.title)}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SelectOption;
